import React, { useState, useRef, useEffect, useCallback } from "react";
import { Play, Pause } from "lucide-react";
import { Button } from "src/components/ui/button";
import { cn } from "src/lib/utils";

export const AudioPlayer = ({ audioUrl, id }) => {
  const [isPlaying, setIsPlaying] = useState(false)
  const [duration, setDuration] = useState(0)
  const [currentTime, setCurrentTime] = useState(0)
  const [isLoaded, setIsLoaded] = useState(false)
  const audioRef = useRef(null)
  const totalBars = 30

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    const setAudioData = () => {
      if (!isNaN(audio.duration) && isFinite(audio.duration)) {
        setDuration(audio.duration)
        setIsLoaded(true)
      }
    }

    const setAudioTime = () => {
      setCurrentTime(audio.currentTime)
    }

    audio.addEventListener("loadedmetadata", setAudioData)
    audio.addEventListener("durationchange", setAudioData)
    audio.addEventListener("timeupdate", setAudioTime)
    audio.addEventListener("ended", handleAudioEnd)

    return () => {
      audio.removeEventListener("loadedmetadata", setAudioData)
      audio.removeEventListener("durationchange", setAudioData)
      audio.removeEventListener("timeupdate", setAudioTime)
      audio.removeEventListener("ended", handleAudioEnd)
    }
  }, [])

  const handleAudioEnd = () => {
    setIsPlaying(false)
    setCurrentTime(0)
    if (audioRef.current) audioRef.current.currentTime = 0
  }

  const togglePlayPause = () => {
    if (!audioRef.current) return

    if (isPlaying) {
      audioRef.current.pause()
    } else {
      audioRef.current.play().catch((error) => {
        console.error("Error playing audio:", error)
      })
    }

    setIsPlaying(!isPlaying)
  }

  const formatTime = (time) => {
    if (isNaN(time) || !isFinite(time)) return "0:00"
    const minutes = Math.floor(time / 60)
    const seconds = Math.floor(time % 60)
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`
  }

  const handleWaveClick = useCallback(
    (index) => {
      const audio = audioRef.current
      // only proceed when audio metadata is loaded and duration is valid
      if (!audio || !isLoaded || duration <= 0 || !isFinite(duration)) return

      // calculate time based on index fraction
      const barCount = totalBars - 1
      const clampedIndex = Math.min(Math.max(0, index), barCount)
      const fraction = clampedIndex / barCount
      const newTime = fraction * duration

      // ensure newTime is finite before setting
      if (!isFinite(newTime)) return

      audio.currentTime = newTime
      setCurrentTime(newTime)
    },
    [duration, isLoaded]
  )

  const generateWaveform = () => {
    return Array.from({ length: totalBars }).map((_, i) => {
      const distanceFromCenter = Math.abs(i - totalBars / 2) / (totalBars / 2)
      const baseHeight = 100 - distanceFromCenter * 60
      const height = baseHeight + Math.random() * 20 - 10
      const isActive = isLoaded && duration > 0
        ? (i / (totalBars - 1)) <= (currentTime / duration)
        : false

      return (
        <div
          key={i}
          onClick={() => handleWaveClick(i)}
          className={cn(
            "w-1 rounded-full transition-all duration-300 ease-out cursor-pointer",
            isActive ? "bg-primary" : "bg-primary/20",
            isPlaying && isActive ? "animate-pulse" : ""
          )}
          style={{
            height: `${Math.max(20, Math.min(100, height))}%`,
            animationDelay: `${i * 0.05}s`,
          }}
        />
      )
    })
  }

  const waveformBars = generateWaveform()

  return (
    <div className="flex flex-col gap-2 p-3 bg-card rounded-xl border shadow-sm w-full max-w-[300px]">
      <div className="flex items-center gap-3">
        <Button
          variant="outline"
          size="icon"
          className={cn(
            "h-10 w-10 rounded-full flex-shrink-0 transition-all duration-300 text-primary",
            isPlaying ? "bg-primary text-primary-foreground" : "bg-secondary"
          )}
          onClick={togglePlayPause}
        >
          {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
        </Button>

        <div className="flex-1">
          <div className="h-10 flex items-center gap-[2px]">{waveformBars}</div>
        </div>
      </div>

      <div className="flex justify-between text-xs px-1">
        <span className="text-muted-foreground">{formatTime(currentTime)}</span>
        <span className="text-muted-foreground">
          {isLoaded ? formatTime(duration) : "--:--"}
        </span>
      </div>

      <audio
        ref={audioRef}
        src={audioUrl}
        preload="metadata"
        onEnded={handleAudioEnd}
        className="hidden"
      />
    </div>
  )
}

